// direct copy from globalpedia/components/COSFileList/index.tsx 
// but we add classname and style prop
// add enableActions prop to control the actions visibility (by default is true)
// case-insensitive file handling for preview

import React, { useState } from 'react';
import { useDeepCompareEffect } from 'ahooks';
import { IFileListProps } from '@gp/components/FileList';
import FileList from '@sg/components/FileList';
import FileItemActions from '@sg/components/FileItemActions';
import useCOS from '@gp/hooks/useCos/useCOS';
import { getBIDSTencentQCouldMassage } from '@sg/services/cos';
import { getBasePath } from '@/utils/utils';
import { openTabPage, downloadFileByLinker, reportBtnOperationLog } from '@gp/utils/common';
// @ts-ignore
import { copyContent } from '@gp/utils/utils';
import { getFileKey } from '@gp/components/FileList/utils';
import { COMMON_IMAGE_SUFFIXES } from '@gp/constants/const';
import { ENV } from '@home/_config/config';
import type { IBaseProps } from '@home/types/common';
import type { IRecord } from '@gp/types/global';
import type { IFile } from '@gp/components/FileList/types';

interface ICOSFileListProps extends IBaseProps, IFileListProps {
  // 预览类型，不同的预览类型 对应 不同的预览页面
  domain: 'weekly_project_highlight' | 'mgmt/studio/board_governance' | 'mgmt/studio/in_dev/gr_management' | 'mgmt/studio/in_dev/notes' | 'mgmt/publishing/project/publishing_reviews' | (string & {});
  // 预览页面url的查询参数对象
  urlSearchParams?: IRecord<string | number>;
  // cos的配置，主要是获取cos的基础配置的服务，比如Region、Bucket、临时token这些信息
  cosOptions?: IUseCOSOptions;
  fullWidth?: boolean;
  autoWrap?: boolean; // 如果为 true，文件宽度自适应内容，最大100%，自动换行
  // 打点配置
  logKeys?: {
    // 预览打点的key
    previewLogKey: string;
    // 下载打点的key
    downloadLogKey: string;
    // 分享打点的key
    shareLogKey: string;
  };
  enableActions?: boolean;
  // 控制显示哪些操作按钮（默认全部显示）
  visibleActions?: {
    preview?: boolean;
    download?: boolean;
    share?: boolean;
  };
  // 可选：自定义 COS domain 参数（用于特殊场景，如 indev 页面需要传递额外参数）
  // 例如: 'gate_review_material,mgmt/studio/in_dev/gr_management'
  cosConfigDomain?: string;
  // 自定义文件项宽度（默认: 250）
  fileItemWidth?: number;
}

const COSFileList: React.FC<ICOSFileListProps> = (props) => {
  // console.log('COSFileList-props', props);

  // 全球加速，预发布和正式
  const controlledEnv = ['pre', 'pro'];
  const isUseAccelerate = controlledEnv.includes(ENV);

  const {
    domain = 'milestone_materials',
    urlSearchParams,
    cosOptions,
    logKeys,
    files,
    className,
    style,
    fullWidth = false,
    autoWrap = false,
    enableActions = true,
    visibleActions = { preview: true, download: true, share: true },
    cosConfigDomain,
    fileItemWidth,
  } = props;
  const { previewLogKey, downloadLogKey, shareLogKey } = logKeys || {};

  const basePath = getBasePath();

  // Determine the actual domain to use for COS config
  // Priority: cosConfigDomain > domain
  const actualCosConfigDomain = cosConfigDomain || domain;

  // Create wrapper functions that pass domain to getBIDSTencentQCouldMassage
  const DEFAULT_COS_OPTIONS: IUseCOSOptions = {
    useAccelerate: isUseAccelerate,
    baseCOSConfigFetcher: () => getBIDSTencentQCouldMassage(actualCosConfigDomain),
    authorizationFetcher: (_options?: any) => getBIDSTencentQCouldMassage(actualCosConfigDomain),
  };

  const { getObjectUrl, getImagePreviewUrl } = useCOS(cosOptions || DEFAULT_COS_OPTIONS);

  // Old approach: controlled preview bubble visibility via file name hover
  // const [previewTipsVisibles, setPreviewTipsVisibles] = useState<IRecord<true | undefined>>({
  //   '申请coding账号.doc': true,
  // });
  const [copyTipses, setCopyTipses] = useState<IRecord<string>>({});
  const [copyTipsVisibles, setCopyTipsVisibles] = useState<IRecord<true | undefined>>({});

  const getPreviewerUrl = async (path?: string, name?: string, format?: string): Promise<string> => {
    // CRITICAL FIX: Convert format to lowercase for case-insensitive comparison
    // This ensures files with .JPG, .PNG work the same as .jpg, .png
    const normalizedFormat = (format || '').toLowerCase();
    
    if (COMMON_IMAGE_SUFFIXES.includes(normalizedFormat)) {
      const { url: imagePreviewUrl } = await getImagePreviewUrl({ key: path || '' });
      return imagePreviewUrl;
    }
    const { protocol, host } = window.location;
    const urlSearchParamsTmp = new URLSearchParams({
      ...urlSearchParams,
      // 预览类型，用于区分进入对应的预览页
      previewer_type: domain,
      // 预览文档的COS存储path
      doc_key: path || '',
      // 预览文档的名称
      doc_name: name || '',
      // 预览文档的文件后缀 (use normalized lowercase format for consistency)
      doc_suffix: normalizedFormat,
    });
    return `${protocol}//${host}${basePath}/strategic/DocPreviewer?${urlSearchParamsTmp.toString()}`;
  };

  const handlePreview = async (file?: IFile | null) => {
    const { path, name, format } = (file || {}) as any;
    const url = await getPreviewerUrl(path, name, format);
    reportBtnOperationLog(previewLogKey, { extInfo: path });
    openTabPage(url);
  };

  const handleDownload = async (file?: IFile | null) => {
    const { path } = (file || {}) as any;
    if (!path) return;
    const fileKey = getFileKey(file);
    try {
      const { downloadUrl } = await getObjectUrl({ key: path });
      downloadFileByLinker(`${downloadUrl};filename=${fileKey}`, 'true');
    } catch (exception) {
      console.warn(exception);
    }
    reportBtnOperationLog(downloadLogKey, { extInfo: path });
  };

  const handleShare = async (file?: IFile | null) => {
    const fileKey = getFileKey(file);
    const { path, name, format } = (file || {}) as any;
    const url = await getPreviewerUrl(path, name, format);
    const copyRes = copyContent(url);
    reportBtnOperationLog(shareLogKey, { extInfo: path });
    if (copyRes === 1) {
      const copyTipsesTmp = {
        ...copyTipses,
        [fileKey]: 'Copy successfully!',
      };
      const copyTipsVisiblesTmp = {
        ...copyTipsVisibles,
        [fileKey]: true as true,
      };
      setCopyTipses(copyTipsesTmp);
      setCopyTipsVisibles(copyTipsVisiblesTmp);
    } else {
      // console.log('copy failed...');
    }
  };

  const renderActions = (data?: IFile | null) => {
    if (!enableActions) {
      return null;
    }
    const key = getFileKey(data);
    // Old approach: get previewTipsVisible from state
    // const previewTipsVisible = previewTipsVisibles[key];
    const copyTips = copyTipses[key];
    const copyTipsVisible = copyTipsVisibles[key];

    return (
      <FileItemActions
        previewTipsVisible={undefined}
        copyTips={copyTips}
        copyTipsVisible={copyTipsVisible}
        visibleActions={visibleActions}
        onPreview={() => {
          handlePreview(data);
        }}
        onDownload={() => {
          handleDownload(data);
        }}
        onShare={() => {
          handleShare(data);
        }}
      />
    );
  };

  useDeepCompareEffect(() => {
    if (!Array.isArray(files) || !files.length) return;

    // Old approach: initialize previewTipsVisibles state
    // const previewTipsVisiblesTmp: IRecord<true | undefined> = {};
    const copyTipsesTmp: IRecord<string> = {};
    const copyTipsVisiblesTmp: IRecord<true | undefined> = {};

    files.forEach((file) => {
      const fileKey = getFileKey(file);
      // previewTipsVisiblesTmp[fileKey] = undefined;
      copyTipsesTmp[fileKey] = 'Copy Link';
      copyTipsVisiblesTmp[fileKey] = undefined;
    });

    // setPreviewTipsVisibles(previewTipsVisiblesTmp);
    setCopyTipses(copyTipsesTmp);
    setCopyTipsVisibles(copyTipsVisiblesTmp);

    return () => {
      // setPreviewTipsVisibles({});
      setCopyTipses({});
      setCopyTipsVisibles({});
    };
  }, [files]);

  return (
    <FileList
      className={className}
      style={style}
      renderActions={enableActions ? renderActions : undefined}
      files={files}
      fullWidth={fullWidth}
      autoWrap={autoWrap}
      fileItemWidth={fileItemWidth}
      onFileNameClick={
        enableActions
          ? (file?: IFile | null) => {
              handlePreview?.(file);
            }
          : undefined
      }
      // Old approach: show preview bubble when hovering over file name
      // onFileNameMouseEnter={
      //   enableActions
      //     ? (file?: IFile | null) => {
      //         const fileKey = getFileKey(file);
      //         const previewTipsVisiblesTmp = {
      //           ...previewTipsVisibles,
      //           [fileKey]: true as true,
      //         };
      //         setPreviewTipsVisibles(previewTipsVisiblesTmp);
      //       }
      //     : undefined
      // }
      // onFileNameMouseLeave={
      //   enableActions
      //     ? (file?: IFile | null) => {
      //         const fileKey = getFileKey(file);
      //         const previewTipsVisiblesTmp = {
      //           ...previewTipsVisibles,
      //           [fileKey]: undefined,
      //         };
      //         setPreviewTipsVisibles(previewTipsVisiblesTmp);
      //       }
      //     : undefined
      // }
      onFileItemMouseLeave={
        enableActions
          ? (file?: IFile | null) => {
              const fileKey = getFileKey(file);
              const copyTipsesTmp = {
                ...copyTipses,
                [fileKey]: 'Copy Link',
              };
              const copyTipsVisiblesTmp = {
                ...copyTipsVisibles,
                [fileKey]: undefined,
              };
              setCopyTipses(copyTipsesTmp);
              setCopyTipsVisibles(copyTipsVisiblesTmp);
            }
          : undefined
      }
    />
  );
};

export default COSFileList;
